import { hasFailingFeedback } from '@Admin/AdminQuotingPage/logic'
import { FORM } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/ConfigurationMenu/formConfig'
import styles from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/ConfigurationMenu/PostProcessSection/PostProcessSummaryCard/styles.css'
import { DRAWING_ANALYSIS_KEY } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import { getFormValues } from 'redux-form'
import { createSelector } from 'reselect'

import { PDFViewerFLags } from '~/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/FilesTab/ConfigurationModal/PreviewPanel/PreviewItem/PDFViewer/logic'
import getColor from '~/lib/getColor'
import { isDfmFailure } from '~/lib/isDfmFailure'
import isFlagEnabled from '~/lib/isFlagEnabled'
import NoNullFields from '~/lib/NoNullFields'
import {
    SECONDARY_PROCESS_ID_CERTIFICATE_OF_COMPLIANCE,
    SECONDARY_PROCESS_ID_MATERIAL_CERTIFICATION,
} from '~/lib/partsConstants'
import { QUOTE_MODEL_REVISION_STATES } from '~/lib/quoteConstants'
import { isCustomQuoteLocked, isQuoteTypeIm } from '~/lib/quoteStatuses'
import Subset from '~/lib/Subset'
import {
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData as ModelAttachmentOcrData,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_allReamableOneSidedTolerances as AllReamableOneSidedTolerances,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_allReamableTwoSidedTolerances as AllReamableTwoSidedTolerances,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_certificateOfConformanceNotes as CertificateOfConformance,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_detectionBoundingBoxes as DetectionBoundingBox,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_foundThreadsInfo as FoundThreadsInfo,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_foundThreadsInfo_detections as FoundThreadsDetection,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_materialCertificateNotes as MaterialCertificate,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_oneSidedTolerances as OneSidedTolerance,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_sanitizedBoundingBoxes as AutoSanitizationBoundingBox,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_standardDimensions as StandardDimension,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_toleranceBlocks as ToleranceBlockData,
    getByQuoteModelRevisionAttachmentId_getModelAttachmentOcrData_twoSidedTolerances as TwoSidedTolerance,
} from '~/redux-modules/dfm-layers/__generated__/getByQuoteModelRevisionAttachmentId'
import { getUserIdentity } from '~/redux-modules/identity'
import colors from '~/styles/_colors.css'
import { FictivStateType } from '../fictivStateType'
import { isQmrPage } from '~/core/hooks/routes/isQmrPage'

import {
    AnalyzingData,
    LoadingData,
    TechnicalDrawingAlertType,
    TechnicalDrawingSectionType,
    UploadLoadingData,
} from './types'

export const technicalDrawingSectionColor = createSelector(
    (state: FictivStateType) => state.dfmLayers.technicalDrawingSectionType,
    (type: TechnicalDrawingSectionType) => {
        if (type === TechnicalDrawingSectionType.success) {
            return colors.successBackground
        } else {
            return colors.infoBackground
        }
    },
)

export const technicalDrawingAlertType = createSelector(
    (state: FictivStateType) => state.dfmLayers.technicalDrawingSectionType,
    (type: TechnicalDrawingSectionType) => {
        if (type === TechnicalDrawingSectionType.success) {
            return TechnicalDrawingAlertType.success
        } else {
            return TechnicalDrawingAlertType.info
        }
    },
)

/**
 * @deprecated Assume flag is always true
 */
export const isQueryRefactorEnabledSuccess = (state: FictivStateType) =>
    isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.QUOTE_DETAILS_QUERY_REFACTOR,
        'true',
    )
export const displayCertificateRequirementHighlights = (
    state: FictivStateType,
) =>
    isFlagEnabled(
        getUserIdentity(state),
        PDFViewerFLags.DISAPLAY_CERTIFICATE_REQUIREMENT_HIGHLIGHTS,
        'true',
    )

export const isDfmLayersDismissConfigurableRequirementsSelector = (
    state: FictivStateType,
) =>
    isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.DFM_LAYERS_DISMISS_CONFIGURABLE_REQUIREMENTS,
        'true',
    )

export const isPdfocrAutoSanitizationEnabledSelector = (
    state: FictivStateType,
) =>
    isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.PDFOCR_AUTO_SANITIZATION,
        'true',
    )

/* This selector was specifically made to create the current ViewModel of the src/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/FilesTab/ConfigurationModal/ConfigurationMenu/TechnicalDrawingAndHolesSection/DrawingSelectionOption.tsx component.
Over time, all logic should eventually go here so that this component only has to render the data it's given.
createSelector allows you to use combinations of existing selectors to create you own,
so you can do code reuse and avoid universal functions.
*/
export const drawingSelectionOptionState = createSelector(
    isQueryRefactorEnabledSuccess,
    technicalDrawingSectionColor,
    technicalDrawingAlertType,
    (state: FictivStateType) => state.dfmLayers.selectedQuoteModelRevisions,
    (state: FictivStateType) => state.dfmLayers.attachingloading,
    (state: FictivStateType) => state.dfmLayers.uploadingData,
    (state: FictivStateType) => state.dfmLayers.analyzingData,
    (
        isQueryRefactorEnabled,
        drawingSectionColor,
        modalAlertType,
        selectedQuoteModelRevisions,
        attachingloading,
        uploadingData,
        analyzingData,
    ) => {
        const selectedQuoteModelRevision = selectedQuoteModelRevisions[0]
        const modelConfiguration =
            selectedQuoteModelRevision?.modelConfiguration
        const dfmFailure = isDfmFailure(selectedQuoteModelRevision?.dfmFeedback)

        const isDrawingAttached = !!selectedQuoteModelRevision?.attachmentLink
        const shouldShowDrawingAnalysisButton =
            isDrawingAttached && analyzingData === 0 && modelConfiguration
        const shouldShowAction =
            attachingloading === LoadingData.hide &&
            analyzingData === AnalyzingData.hide &&
            uploadingData === UploadLoadingData.hide
        const shouldNotShowAction = attachingloading === LoadingData.show

        return {
            dfmFailure,
            technicalDrawingSectionColor: drawingSectionColor,
            technicalDrawingAlertType: modalAlertType,
            shouldShowDrawingAnalysisButton,
            selectedQuoteModelRevision,
            attachingloading,
            uploadingData,
            analyzingData,
            shouldShowAction,
            shouldNotShowAction,
            isDrawingAttached,
            isQueryRefactorEnabled,
        }
    },
)

export const attachAndUploadDrawingSectionSelector = createSelector(
    getUserIdentity,
    (state: FictivStateType) => state.dfmLayers.selectedQuoteModelRevisions,
    (state: FictivStateType) => state.dfmLayers.quote,

    (identity, selectedQuoteModelRevisions, quote) => {
        const buttonColumnStyles = { styles: { width: '100%' } }
        const selectedQuoteModelRevision = selectedQuoteModelRevisions[0]

        return {
            identity,
            buttonColumnStyles,
            selectedQuoteModelRevision,
            quote,
        }
    },
)

export const isDrawingReviewedByAdminSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers?.selectedQuoteModelRevisions,
    selectedQuoteModelRevisions => {
        const selectedQuoteModelRevision = selectedQuoteModelRevisions?.[0]

        const { isPublished, quotedBy, updatedBy, quoteUpdatedBy } =
            selectedQuoteModelRevision?.modelConfiguration?.dfmInspection || {}

        return !!isPublished && !!quotedBy && !!updatedBy && !!quoteUpdatedBy
    },
)

export const attachmentDropdownSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers?.analyzingData,
    (state: FictivStateType) => state.dfmLayers?.selectedQuoteModelRevisions,
    (analyzingData, selectedQuoteModelRevisions) => {
        const selectedQuoteModelRevision =
            selectedQuoteModelRevisions && selectedQuoteModelRevisions[0]
        const estimatedPrice =
            selectedQuoteModelRevision?.processesDetails?.totalPrice

        return {
            analyzingData,
            selectedQuoteModelRevision,
            estimatedPrice,
            isQueryRefactorEnabled: true,
        }
    },
)

export const configurationMenuSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers?.selectedQuoteModelRevisions,
    selectedQuoteModelRevisions => {
        return {
            ready: !!selectedQuoteModelRevisions?.length,
        }
    },
)

const PDFOCRSelectorNullObject = {
    tolerances: [],
    threads: [],
    attachmentLinkId: undefined,
}

type PDFOCRAnnotation = {
    coordinate: {
        x: number
        y: number
        page: number
    }
    boundingBox: {
        xmin: number
        xmax: number
        ymax: number
        ymin: number
        pageNumber: number
    }
    text?: string
}

type DetectionBoundingBoxes = ModelAttachmentOcrData['detectionBoundingBoxes']

// the subset is the intersection of the detectionBoundingBox and the sanitizedBoundingBox
type RequiredBoundingBox = Subset<
    NoNullFields<DetectionBoundingBox>,
    {
        pageNumber: number
        xmax: number
        xmin: number
        ymax: number
        ymin: number
    }
>

const isRequiredBoundingBox = (
    detectionBoundingBox: DetectionBoundingBox | null | RequiredBoundingBox,
): detectionBoundingBox is RequiredBoundingBox => {
    if (!detectionBoundingBox) {
        return false
    }

    const { xmax, xmin, ymax, ymin, pageNumber } = detectionBoundingBox

    if (
        xmax === null ||
        xmin === null ||
        ymax === null ||
        ymin === null ||
        pageNumber === null
    ) {
        return false
    }

    return true
}

const requiredDetectionBoundingBox = (
    detectionBoundingBoxes: DetectionBoundingBoxes,
    index: number | null,
): RequiredBoundingBox | null => {
    if (index === null || detectionBoundingBoxes === null) {
        return null
    }
    const detectionBoundingBox = detectionBoundingBoxes[index]

    if (!isRequiredBoundingBox(detectionBoundingBox)) {
        return null
    } else {
        return detectionBoundingBox
    }
}

const boundingBoxToAnnotation = (
    detectionBoundingBox: RequiredBoundingBox,
): PDFOCRAnnotation => {
    const { xmax, xmin, ymax, ymin, pageNumber } = detectionBoundingBox

    const coordinate = {
        x: (xmax + xmin) / 2,
        y: (ymax + ymin) / 2,
        page: pageNumber + 1,
    }

    const boundingBox = {
        xmax,
        xmin,
        ymax,
        ymin,
        pageNumber: pageNumber + 1,
    }

    return {
        boundingBox,
        coordinate,
    }
}

export const PDFOCRDataSelector = createSelector(
    (state: FictivStateType): ModelAttachmentOcrData | null =>
        state.dfmLayers.PDFOCRData,
    isDrawingReviewedByAdminSelector,
    (state: FictivStateType) =>
        state?.dfmLayers?.selectedQuoteModelRevisions?.[0]?.state ===
        'needs_rfq',
    (state: FictivStateType) =>
        // selectedQuoteModelRevisions can be [quoteDetails_quote_quoteModels_items_selectedQuoteModelRevision] for viewers or editors
        // and [adminQuoteModelRevision_quoteModelRevision] for schedulers. Only adminQuoteModelRevision_quoteModelRevision contians manufacturingDrawing
        // ignoring here for now same as in the AdminQuotingPage
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state.dfmLayers?.selectedQuoteModelRevisions?.[0]?.manufacturingDrawing,
    (
        data,
        isDrawingReviewedByAdmin,
        selectedQMRIsRFQ,
        manufacturingDrawing,
    ) => {
        const onQmrPage = isQmrPage()

        if (!data) {
            return PDFOCRSelectorNullObject
        }

        const drawingWasSanitized =
            manufacturingDrawing?.__typename === 'SanitizedAttachment'

        const {
            oneSidedTolerances,
            twoSidedTolerances,
            allReamableOneSidedTolerances,
            allReamableTwoSidedTolerances,
            detectionBoundingBoxes,
            foundThreadsInfo,
            standardDimensions,
            certificateOfConformanceNotes,
            materialCertificateNotes,
            sanitizedBoundingBoxes,
            toleranceBlocks,
            id,
        } = data

        // check for old quotes that do not have fully formed PDFOCR
        if (
            !detectionBoundingBoxes ||
            !foundThreadsInfo ||
            (!onQmrPage && (selectedQMRIsRFQ || isDrawingReviewedByAdmin))
        ) {
            return { tolerances: [], threads: [], attachmentLinkId: id }
        }

        type Tolerance =
            | OneSidedTolerance
            | TwoSidedTolerance
            | AllReamableOneSidedTolerances
            | AllReamableTwoSidedTolerances

        type RequiredTolerance = NoNullFields<Tolerance>

        const tolerances: Array<PDFOCRAnnotation> = []

        ;[
            ...(oneSidedTolerances || []),
            ...(twoSidedTolerances || []),
            ...(allReamableOneSidedTolerances || []),
            ...(allReamableTwoSidedTolerances || []),
        ]
            .filter((tolerance): tolerance is RequiredTolerance => {
                const nominalCoordinate = tolerance?.nominalCoordinate

                return (
                    !!tolerance &&
                    tolerance.boundingBox !== null &&
                    !!nominalCoordinate &&
                    nominalCoordinate.page !== null &&
                    nominalCoordinate.x !== null &&
                    nominalCoordinate.y !== null
                )
            })
            .forEach((tolerance: RequiredTolerance) => {
                const { nominalCoordinate } = tolerance

                const coordinate = {
                    x: nominalCoordinate.x,
                    y: nominalCoordinate.y,
                    page: nominalCoordinate.page + 1,
                }

                const detectionBoundingBox = requiredDetectionBoundingBox(
                    detectionBoundingBoxes,
                    tolerance.boundingBox,
                )

                if (detectionBoundingBox === null) {
                    return
                }

                const boundingBox = {
                    xmax: detectionBoundingBox.xmax,
                    xmin: detectionBoundingBox.xmin,
                    ymax: detectionBoundingBox.ymax,
                    ymin: detectionBoundingBox.ymin,
                    pageNumber: detectionBoundingBox.pageNumber + 1,
                }

                tolerances.push({
                    coordinate,
                    boundingBox,
                })
            })

        const threads: Array<PDFOCRAnnotation> = []

        ;(foundThreadsInfo || []).forEach(
            (threadInfo: FoundThreadsInfo | null) => {
                if (threadInfo === null || threadInfo.detections === null) {
                    return
                }

                threadInfo.detections.forEach(
                    (detection: FoundThreadsDetection | null) => {
                        if (
                            detection === null ||
                            detection.boundingBox === null
                        ) {
                            return
                        }

                        const detectionBoundingBox =
                            requiredDetectionBoundingBox(
                                detectionBoundingBoxes,
                                detection.boundingBox,
                            )

                        if (detectionBoundingBox === null) {
                            return
                        }

                        const annotation =
                            boundingBoxToAnnotation(detectionBoundingBox)

                        threads.push(annotation)
                    },
                )
            },
        )

        const GDTTolerances: Array<PDFOCRAnnotation> = []

        ;(toleranceBlocks || []).forEach(
            (toleranceBlock: ToleranceBlockData | null) => {
                if (!toleranceBlock) {
                    return
                }

                if (!toleranceBlock.boundingBox) {
                    return
                }

                if (!toleranceBlock.symbol) {
                    return
                }

                const detectionBoundingBox = requiredDetectionBoundingBox(
                    detectionBoundingBoxes,
                    toleranceBlock.boundingBox,
                )

                if (!detectionBoundingBox) {
                    return
                }

                const annotation = boundingBoxToAnnotation(detectionBoundingBox)

                annotation.text = toleranceBlock.symbol
                GDTTolerances.push(annotation)
            },
        )

        const standardTolerances: Array<PDFOCRAnnotation> = []

        ;(standardDimensions || []).forEach(
            (dimension: StandardDimension | null) => {
                if (dimension === null || dimension.boundingBox === null) {
                    return
                }
                const detectionBoundingBox = requiredDetectionBoundingBox(
                    detectionBoundingBoxes,
                    dimension.boundingBox,
                )

                if (detectionBoundingBox === null) {
                    return
                }

                const annotation = boundingBoxToAnnotation(detectionBoundingBox)

                standardTolerances.push(annotation)
            },
        )
        const certificateOfConformanceWithBoundingBox: Array<PDFOCRAnnotation> =
            []

        ;(certificateOfConformanceNotes || []).forEach(
            (certificate: CertificateOfConformance | null) => {
                if (certificate === null || certificate.boundingBox === null) {
                    return
                }
                const detectionBoundingBox = requiredDetectionBoundingBox(
                    detectionBoundingBoxes,
                    certificate.boundingBox,
                )

                if (detectionBoundingBox === null) {
                    return
                }

                const annotation = boundingBoxToAnnotation(detectionBoundingBox)

                certificateOfConformanceWithBoundingBox.push(annotation)
            },
        )

        const materialCertificationWithBoundingBox: Array<PDFOCRAnnotation> = []

        ;(materialCertificateNotes || []).forEach(
            (certificate: MaterialCertificate | null) => {
                if (certificate === null || certificate.boundingBox === null) {
                    return
                }
                const detectionBoundingBox = requiredDetectionBoundingBox(
                    detectionBoundingBoxes,
                    certificate.boundingBox,
                )

                if (detectionBoundingBox === null) {
                    return
                }

                const annotation = boundingBoxToAnnotation(detectionBoundingBox)

                materialCertificationWithBoundingBox.push(annotation)
            },
        )

        const redactionBoxes: Array<PDFOCRAnnotation> = []

        ;((!drawingWasSanitized && sanitizedBoundingBoxes) || []).forEach(
            (boundingBox: AutoSanitizationBoundingBox | null) => {
                if (boundingBox === null) {
                    return
                }
                if (!isRequiredBoundingBox(boundingBox)) {
                    return
                }

                const annotation = boundingBoxToAnnotation(boundingBox)

                redactionBoxes.push(annotation)
            },
        )
        return {
            tolerances,
            threads,
            standardTolerances,
            materialCertificationWithBoundingBox,
            certificateOfConformanceWithBoundingBox,
            redactionBoxes,
            attachmentLinkId: id,
            GDTTolerances,
        }
    },
)

export const AutoDetectedPdfOcrFeatures = createSelector(
    PDFOCRDataSelector,
    displayCertificateRequirementHighlights,
    (PDFOCRData, shouldHighLightMaterialCertificationAndCOC) => {
        const threads = PDFOCRData.threads
        const tolerances = PDFOCRData.tolerances
        const standardTolerances = PDFOCRData.standardTolerances

        return {
            threads,
            tolerances,
            standardTolerances,
            shouldHighLightMaterialCertificationAndCOC,
        }
    },
)

export const drawingAnalysisMenuSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers?.quote,
    (state: FictivStateType) => state.dfmLayers?.selectedQuoteModelRevisions,
    (state: FictivStateType) => getFormValues(FORM)(state),
    (state: FictivStateType) => state.dfmLayers?.webviewerInstance,
    (state: FictivStateType) => state.dfmLayers?.PDFOCRData,
    (state: FictivStateType) =>
        state?.dfmLayers?.selectedQuoteModelRevisions?.[0]?.state ===
        'needs_rfq',
    isDrawingReviewedByAdminSelector,
    isDfmLayersDismissConfigurableRequirementsSelector,
    displayCertificateRequirementHighlights,
    () => true,
    (state: FictivStateType) => state.quoteQllt?.lastActivityPerformed,
    (
        quote,
        selectedQuoteModelRevisions,
        selectedValues,
        webviewerInstance,
        PDFOCRData,
        selectedQMRIsRFQ,
        isDrawingReviewedByAdmin,
        isDfmLayersDismissConfigurableRequirements,
        shouldHighLightMaterialCertificationAndCOC,
        isQueryRefactorEnabled,
        lastActivityPerformed,
    ) => {
        const isQuoteLocked = isCustomQuoteLocked(quote)
        const isImQuoteType = isQuoteTypeIm(quote?.type)
        const bulkConfig = selectedQuoteModelRevisions?.length > 1
        const quoteId = quote?.id
        const selectedQuoteModelRevision =
            selectedQuoteModelRevisions && selectedQuoteModelRevisions[0]
        const estimatedPrice =
            selectedQuoteModelRevision?.processesDetails?.totalPrice

        const isPDFOCRData =
            !!PDFOCRData?.oneSidedTolerances?.length ||
            !!PDFOCRData?.twoSidedTolerances?.length ||
            !!PDFOCRData?.allReamableOneSidedTolerances?.length ||
            !!PDFOCRData?.allReamableTwoSidedTolerances?.length
        const needsRfq =
            QUOTE_MODEL_REVISION_STATES.NEEDS_RFQ ===
            selectedQuoteModelRevision?.state
        const showFailureDfmAlert =
            !bulkConfig &&
            hasFailingFeedback(selectedQuoteModelRevision?.dfmFeedback || [])
        const shouldHideDetectedRequirements =
            !needsRfq && !showFailureDfmAlert && !isDrawingReviewedByAdmin

        const shouldHighLightAdditionalRequirements =
            shouldHighLightMaterialCertificationAndCOC &&
            shouldHideDetectedRequirements

        return {
            lastActivityPerformed,
            quote,
            selectedQuoteModelRevision,
            estimatedPrice,
            selectedQuoteModelRevisions,
            isQuoteLocked,
            shouldHighLightAdditionalRequirements,
            isImQuoteType,
            bulkConfig,
            quoteId,
            selectedValues,
            webviewerInstance,
            isPDFOCRData,
            selectedQMRIsRFQ,
            isDrawingReviewedByAdmin,
            isDfmLayersDismissConfigurableRequirements,
            isQueryRefactorEnabled,
        }
    },
)

export const GenericPostProcessSelector = createSelector(
    displayCertificateRequirementHighlights,
    (state: FictivStateType) => state.dfmLayers.selectedTabName,

    (shouldHighLightMaterialCertificationAndCOC, selectedTabName) => {
        const fontSizeValue = '14px'
        const fontWeightValue = 500
        const colorValue = getColor('blackAlpha45')
        const lineHeightValue = '22px'
        const widthValue = '74px'
        const heightValue = '32px'
        const shouldHighLightDetectedRequirements =
            !shouldHighLightMaterialCertificationAndCOC ||
            (shouldHighLightMaterialCertificationAndCOC &&
                selectedTabName !== DRAWING_ANALYSIS_KEY)

        const AdditionalRequirementsStyle =
            shouldHighLightMaterialCertificationAndCOC &&
            selectedTabName === DRAWING_ANALYSIS_KEY
                ? `${styles.PostProcessCardWrapper} ${styles.PostProcess}`
                : `${styles.postProcessCardWrapper} ${styles.postProcessCard}`

        return {
            fontSizeValue,
            fontWeightValue,
            colorValue,
            lineHeightValue,
            shouldHighLightMaterialCertificationAndCOC,
            shouldHighLightDetectedRequirements,
            AdditionalRequirementsStyle,
            widthValue,
            heightValue,
            isQueryRefactorEnabled: true,
        }
    },
)

export const ConfigurationModalHeaderSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers.generatedDrawing,
    isQueryRefactorEnabledSuccess,
    (generatedDrawing, isQueryRefactorEnabled) => ({
        generatedDrawing,
        isQueryRefactorEnabled,
    }),
)

export const pdfViewerSelector = createSelector(
    displayCertificateRequirementHighlights,
    isPdfocrAutoSanitizationEnabledSelector,
    (state: FictivStateType) => state.dfmLayers.showRFQModal,
    (state: FictivStateType) => state.dfmLayers.userName,
    (state: FictivStateType) =>
        state.dfmLayers.obfuscateFictivAnnotationAttribution,
    (state: FictivStateType) => state.dfmLayers.pdfUrl,
    (state: FictivStateType) =>
        state.dfmLayers.selectedQuoteModelRevisions[0]?.attachmentLink
            ?.quoteAttachmentRevision?.attachment?.id,
    (state: FictivStateType) =>
        state.dfmLayers.selectedQuoteModelRevisions[0]?.id,
    (state: FictivStateType) =>
        state.dfmLayers.selectedQuoteModelRevisions[0]?.attachmentLink?.id,
    (state: FictivStateType) => state.dfmLayers.qmrAttachmentAnnotations?.data,

    (
        shouldHighLightMaterialCertificationAndCOC,
        isPdfocrAutoSanitizationEnabled,
        showRFQModal,
        userName,
        obfuscateFictivAnnotationAttribution,
        pdfUrl,
        attachmentId,
        quoteModelRevisionId,
        attachmentLinkId,
        qmrAttachmentAnnotations,
    ) => {
        return {
            showRFQModal,
            userName,
            obfuscateFictivAnnotationAttribution,
            pdfUrl,
            shouldHighLightMaterialCertificationAndCOC,
            attachmentId,
            quoteModelRevisionId,
            attachmentLinkId,
            qmrAttachmentAnnotations,
            isPdfocrAutoSanitizationEnabled,
        }
    },
)

export const previewItemSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers.qmrWithHolesDetail,
    (state: FictivStateType) => state.dfmLayers.qmrWithDfmFeedBack,
    (state: FictivStateType) => state.dfmLayers.selectedTabName,
    (state: FictivStateType) => state.dfmLayers.editPdfMode,
    (state: FictivStateType) => state.dfmLayers.qmrAttachmentAnnotations,
    (state: FictivStateType) => state.dfmLayers.showLoading,
    (state: FictivStateType) => state.dfmLayers.errorMessage,
    (state: FictivStateType) =>
        state.dfmLayers.selectedQuoteModelRevisions[0]?.partRevision,
    (state: FictivStateType) =>
        state.dfmLayers.selectedQuoteModelRevisions[0]?.dfmFeedback,
    (state: FictivStateType) =>
        state.dfmLayers.selectedQuoteModelRevisions[0]?.holes,

    (
        qmrWithHolesDetail,
        qmrWithDfmFeedBack,
        selectedTab,
        editPdfMode,
        qmrAttachmentAnnotations,
        showLoading,
        errorMessage,
        partRevision,
        dfmFeedback,
        holes,
    ) => {
        return {
            qmrWithHolesDetail,
            qmrWithDfmFeedBack,
            selectedTab,
            editPdfMode,
            qmrAttachmentAnnotations,
            showLoading,
            errorMessage,
            partRevision,
            dfmFeedback,
            holes,
        }
    },
)

export const previewPanelSelector = createSelector(
    (state: FictivStateType) => state.dfmLayers.selectedQuoteModelRevisions,
    (state: FictivStateType) => state.dfmLayers.selectedTabName,
    (state: FictivStateType) => state.dfmLayers.unsavedAnno,

    (
        selectedQuoteModelRevisions,
        selectedTab,

        unsavedAnno,
    ) => {
        return {
            selectedQuoteModelRevisions,
            selectedTab,
            unsavedAnno,
        }
    },
)

export const drawingSelectionOption = createSelector(
    technicalDrawingSectionColor,
    technicalDrawingAlertType,

    (state: FictivStateType) => state.dfmLayers.attachingloading,
    (state: FictivStateType) => state.dfmLayers.uploadingData,
    (state: FictivStateType) => state.dfmLayers.analyzingData,
    (state: FictivStateType) => state.dfmLayers.selectedQuoteModelRevisions,
    (state: FictivStateType) => state.dfmLayers.showDrawingButton,
    (state: FictivStateType) => state.dfmLayers.backgroudWarningColor,

    (
        drawingSectionColor,
        modalAlertType,
        attachingloading,
        uploadingData,
        analyzingData,
        selectedQuoteModelRevisions,
        showDrawingButton,
        backgroudWarningColor,
    ) => {
        const selectedQuoteModelRevision = selectedQuoteModelRevisions[0]

        const { isPublished, quotedBy, updatedBy, quoteUpdatedBy } =
            selectedQuoteModelRevision?.modelConfiguration?.dfmInspection || {}

        const isDrawingReviewedByAdmin =
            !!isPublished && !!quotedBy && !!updatedBy && !!quoteUpdatedBy

        const backgroundColor =
            showDrawingButton === 1 ? backgroudWarningColor : ''

        return {
            technicalDrawingSectionColor: drawingSectionColor,
            technicalDrawingAlertType: modalAlertType,
            attachingloading,
            uploadingData,
            analyzingData,
            isDrawingReviewedByAdmin,
            selectedQuoteModelRevision,
            selectedQuoteModelRevisions,
            showDrawingButton,
            backgroudWarningColor,
            backgroundColor,
        }
    },
)

const getRequiredCertification = (
    cocRequired: boolean | null | undefined,
    materialCertificationRequired: boolean | null | undefined,
) => {
    let requiredCertification: Array<string> = []

    if (cocRequired && materialCertificationRequired) {
        requiredCertification = [
            SECONDARY_PROCESS_ID_CERTIFICATE_OF_COMPLIANCE,
            SECONDARY_PROCESS_ID_MATERIAL_CERTIFICATION,
        ]
    } else if (cocRequired && !materialCertificationRequired) {
        requiredCertification = [SECONDARY_PROCESS_ID_CERTIFICATE_OF_COMPLIANCE]
    } else if (!cocRequired && materialCertificationRequired) {
        requiredCertification = [SECONDARY_PROCESS_ID_MATERIAL_CERTIFICATION]
    }
    return requiredCertification
}

export const additionalRequirementConfigurationSelector = createSelector(
    (state: FictivStateType) => state?.dfmLayers.RAWPDFOCRData,

    drawingSelectionOptionState,
    (state: FictivStateType) => state?.dfmLayers.PDFOCRData,
    (RAWPDFOCRData, quoteDetail, PDFOCRData) => {
        const data = RAWPDFOCRData

        const {
            requiresCertificateOfConformance: cocRequired,
            requiresMaterialCertificate: materialCertificationRequired,
        } = data ?? {}

        const isConfigurationRequired =
            cocRequired || (!cocRequired && materialCertificationRequired)

        const isFinishSelected = PDFOCRData?.callsForOffPlatformFinish

        const { dfmFailure, selectedQuoteModelRevision } = quoteDetail

        const postProcessSelected =
            selectedQuoteModelRevision?.processesDetails?.postProcesses

        const requiredCertification = getRequiredCertification(
            cocRequired,
            materialCertificationRequired,
        )

        const foundConfigurationAdded =
            postProcessSelected?.filter(processList => {
                return requiredCertification?.includes(
                    processList.postProcess.id,
                )
            }) ?? []
        const isConfigurationCompleted =
            foundConfigurationAdded &&
            foundConfigurationAdded?.length === requiredCertification?.length &&
            isFinishSelected

        return {
            RAWPDFOCRData,
            isConfigurationRequired,
            dfmFailure,
            isFinishSelected,
            cocRequired,
            materialCertificationRequired,
            selectedQuoteModelRevision,
            postProcessSelected,
            isConfigurationCompleted,
        }
    },
)

export const dFMDrawingSelectionOptionSelector = createSelector(
    drawingSelectionOptionState,
    drawingSelectionOption,

    (drawingSelectionOptionStateData, drawingSelectionOptionData) => {
        const {
            shouldShowDrawingAnalysisButton,
            selectedQuoteModelRevision,
            dfmFailure,
            shouldShowAction,
            shouldNotShowAction,
        } = drawingSelectionOptionStateData

        const { backgroundColor } = drawingSelectionOptionData

        return {
            shouldShowDrawingAnalysisButton,
            selectedQuoteModelRevision,
            dfmFailure,
            shouldShowAction,
            shouldNotShowAction,
            backgroundColor,
        }
    },
)

export const dFMTechnicalDrawingAndHolesSectionSelector = createSelector(
    isQueryRefactorEnabledSuccess,
    additionalRequirementConfigurationSelector,
    drawingSelectionOption,
    (state: FictivStateType) => state?.dfmLayers.quote,

    (
        isQueryRefactorEnabled,
        additionalReqConfigData,
        drawingSelectionOptionData,
        quote,
    ) => {
        const {
            isConfigurationRequired,
            dfmFailure,
            isConfigurationCompleted,
        } = additionalReqConfigData

        const {
            technicalDrawingSectionColor: drawingSectionColor,
            technicalDrawingAlertType: modalAlertType,
            attachingloading,
            uploadingData,
            analyzingData,
            selectedQuoteModelRevision,
            isDrawingReviewedByAdmin,
            showDrawingButton,
        } = drawingSelectionOptionData

        return {
            isQueryRefactorEnabled,
            isConfigurationRequired,
            dfmFailure,
            isConfigurationCompleted,
            technicalDrawingSectionColor: drawingSectionColor,
            technicalDrawingAlertType: modalAlertType,
            attachingloading,
            uploadingData,
            analyzingData,
            selectedQuoteModelRevision,
            isDrawingReviewedByAdmin,
            showDrawingButton,
            quote,
        }
    },
)
export const dfmSidebarWrapperTabSelector = createSelector(
    isQueryRefactorEnabledSuccess,
    (state: FictivStateType) => state?.dfmLayers?.quote,
    (isQueryRefactorEnabled, quote) => {
        const quoteId = quote?.id

        return {
            isQueryRefactorEnabled,
            quoteId,
        }
    },
)
